"use client"
import React from 'react';
import { ExternalLink } from 'lucide-react';
import Image from 'next/image';

interface PresentedByProps {
    companyName?: string;
    websiteUrl?: string;
    size?: 'sm' | 'md' | 'lg' | 'xl';
    theme?: 'dark' | 'light';
}

export const PresentedBy: React.FC<PresentedByProps> = ({
    companyName = "Dubsol",
    websiteUrl = "https://dubsol.app",
    size = 'xl',
    theme = 'dark'
}) => {
    const sizeConfig = {
        sm: {
            container: "max-w-2xl",
            logo: "h-16",
            text: "text-xl",
            subtitle: "text-sm"
        },
        md: {
            container: "max-w-3xl", 
            logo: "h-24",
            text: "text-2xl",
            subtitle: "text-sm"
        },
        lg: {
            container: "max-w-4xl",
            logo: "h-32",
            text: "text-3xl", 
            subtitle: "text-base"
        },
        xl: {
            container: "max-w-6xl",
            logo: "h-40",
            text: "text-4xl",
            subtitle: "text-lg"
        }
    };

    const themeConfig = {
        dark: {
            background: "bg-black",
            text: {
                primary: "text-white",
                secondary: "text-gray-400",
                accent: "text-yellow-500"
            },
            hover: "hover:text-yellow-500"
        },
        light: {
            background: "bg-white",
            text: {
                primary: "text-gray-900", 
                secondary: "text-gray-600",
                accent: "text-yellow-600"
            },
            hover: "hover:text-yellow-600"
        }
    };

    const currentSize = sizeConfig[size];
    const currentTheme = themeConfig[theme];

    return (
        <div className={`w-full ${currentTheme.background} py-16 px-4`}>
            <div className={`mx-auto text-center ${currentSize.container}`}>
                
                {/* Presented By Text */}
                <div className="flex items-center justify-center gap-3 mb-8">
                    <div className={`w-8 h-px ${currentTheme.text.accent}`}></div>
                    <span className={`${currentTheme.text.secondary} ${currentSize.subtitle} font-medium tracking-wider uppercase`}>
                        Presented By
                    </span>
                    <div className={`w-8 h-px ${currentTheme.text.accent}`}></div>
                </div>

                {/* Logo - SIMPLES E DIRETO */}
                <div className="mb-8">
                    <div className={`${currentSize.logo} mx-auto mb-6 flex items-center justify-center`}>
                        <Image
                            src="/logodubsol.png"
                            alt={`${companyName} Logo`}
                            width={600}
                            height={600}
                            className="object-contain w-auto h-full"
                            priority
                        />
                    </div>

                    {/* Website Link */}
                    <a
                        href={websiteUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`inline-flex items-center gap-2 ${currentTheme.text.secondary} ${currentTheme.hover} transition-colors duration-300 font-medium`}
                    >
                        <span className={currentSize.subtitle}>Visit Website</span>
                        <ExternalLink className="w-4 h-4" />
                    </a>
                </div>
            </div>
        </div>
    );
};